<template>
	<v-app class="login-app">
		<v-main class="login-main">
			<div class="background-grid" aria-hidden="true"></div>

			<v-container fluid class="login-container">
				<section class="login-shell" aria-label="Acesso ao Sistema de Pontos">
					<aside class="brand-panel">
						<div class="brand-accent" aria-hidden="true"></div>
						<div class="brand-content">
							<div class="logo-frame">
								<img src="@/img/logoCRG.jpeg" alt="CRG Elétrica" class="company-logo">
							</div>

							<div class="brand-copy">
								<span class="brand-eyebrow">Gestão de jornada</span>
								<h1>Controle de ponto com clareza e confiança.</h1>
								<p>Acompanhe jornadas, registros e banco de horas em um ambiente seguro e organizado.</p>
							</div>

							<div class="brand-security">
								<v-icon size="18" color="#F3B806">mdi-shield-check-outline</v-icon>
								<span>Acesso corporativo protegido</span>
							</div>
						</div>
					</aside>

					<div class="form-panel">
						<div class="mobile-logo-frame">
							<img src="@/img/logoCRG.jpeg" alt="CRG Elétrica" class="mobile-logo">
						</div>

						<div class="form-content">
							<div class="welcome-icon">
								<v-icon size="26" color="primary">mdi-account-outline</v-icon>
							</div>
							<p class="form-eyebrow">Bem-vindo</p>
							<h2>Acesse sua conta</h2>
							<p class="form-description">Entre para acessar o Sistema de Pontos.</p>

							<form ref="form" class="login-form" @submit.prevent="login()">
								<v-text-field
									v-model="usuario.usuario"
									name="usuario"
									label="Usuário"
									type="text"
									outlined
									prepend-inner-icon="mdi-account-outline"
									autocomplete="username"
									:disabled="loading"
									@keydown.enter.prevent="login"
									class="mb-3"
								/>
								<v-text-field
									v-model="usuario.senha"
									name="senha"
									label="Senha"
									:type="showPassword ? 'text' : 'password'"
									outlined
									prepend-inner-icon="mdi-lock-outline"
									:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
									autocomplete="current-password"
									@click:append="showPassword = !showPassword"
									:disabled="loading"
									@keydown.enter.prevent="login"
									class="mb-2"
								/>

								<v-btn
									type="submit"
									color="primary"
									block
									x-large
									:loading="loading"
									:disabled="!isFormValid"
									class="login-button"
								>
									Entrar
									<v-icon right size="20">mdi-arrow-right</v-icon>
								</v-btn>

								<v-btn text color="secondary" block small @click="forgot" :disabled="loading" class="forgot-button">
									Esqueci a senha
								</v-btn>
							</form>

							<div class="form-security">
								<v-icon size="15" color="#6B7280">mdi-lock-check-outline</v-icon>
								<span>Seus dados são transmitidos com segurança</span>
							</div>
						</div>
					</div>
				</section>

				<p class="page-footer">CRG Elétrica · Sistema de Gestão de Jornada</p>
			</v-container>
		</v-main>
	</v-app>
</template>

<script>
import axios from 'axios'
import { baseApiUrl, showError, userKey } from '@/global'

export default {
	name: 'AuthLogin',

	data() {
		return {
			usuario: {},
			loading: false,
			showPassword: false
		}
	},
	computed: {
		isFormValid() {
			return this.usuario.usuario && this.usuario.senha && this.usuario.usuario.length >= 3 && this.usuario.senha.length >= 4
		}
	},
	methods: {
		async login() {
			if (!this.isFormValid) return

			this.loading = true
			try {
				const loginData = {
					email: this.usuario.usuario,
					senha: this.usuario.senha
				}
				const res = await axios.post(`${baseApiUrl}/signin`, loginData)
				this.$store.commit('setUser', res.data)
				localStorage.setItem(userKey, JSON.stringify(res.data))
				this.$router.push({ path: '/pontos' })
			} catch (error) {
				showError(error)
			} finally {
				this.loading = false
			}
		},
		forgot() {
			this.$router.push({ name: 'recover' })
		}
	}
}
</script>

<style lang="css" scoped>
.login-app {
	min-height: 100vh;
	background: #00004e;
}

.login-main {
	position: relative;
	min-height: 100vh;
	overflow: hidden;
	background:
		radial-gradient(circle at 9% 8%, rgba(243, 184, 6, 0.24), transparent 28%),
		radial-gradient(circle at 91% 88%, rgba(43, 86, 114, 0.62), transparent 36%),
		linear-gradient(135deg, #00003f 0%, #00006f 48%, #173d58 100%);
}

.background-grid {
	position: absolute;
	inset: 0;
	opacity: 0.16;
	background-image:
		linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
		linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
	background-size: 48px 48px;
	-webkit-mask-image: linear-gradient(to bottom, black, transparent 85%);
	mask-image: linear-gradient(to bottom, black, transparent 85%);
	pointer-events: none;
}

.login-container {
	position: relative;
	z-index: 1;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 38px 24px 20px;
}

.login-shell {
	width: 100%;
	max-width: 940px;
	min-height: 570px;
	display: grid;
	grid-template-columns: 0.9fr 1.1fr;
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.22);
	border-radius: 22px;
	background: #ffffff;
	box-shadow: 0 28px 80px rgba(0, 0, 35, 0.38);
}

.brand-panel {
	position: relative;
	overflow: hidden;
	background: linear-gradient(155deg, #000052 0%, #00006f 64%, #102e48 100%);
	color: #ffffff;
}

.brand-panel::after {
	content: '';
	position: absolute;
	right: -108px;
	bottom: -108px;
	width: 280px;
	height: 280px;
	border: 46px solid rgba(243, 184, 6, 0.10);
	border-radius: 50%;
}

.brand-accent {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 5px;
	background: linear-gradient(90deg, #f3b806, #ffd85a);
}

.brand-content {
	position: relative;
	z-index: 1;
	height: 100%;
	display: flex;
	flex-direction: column;
	padding: 54px 48px 42px;
}

.logo-frame,
.mobile-logo-frame {
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	background: #ffffff;
	box-shadow: 0 14px 35px rgba(0, 0, 30, 0.26);
}

.logo-frame {
	width: 260px;
	height: 152px;
	border-radius: 16px;
}

.company-logo {
	width: 258px;
	height: 258px;
	max-width: none;
	flex: 0 0 auto;
	object-fit: contain;
}

.brand-copy {
	margin-top: 52px;
}

.brand-eyebrow,
.form-eyebrow {
	display: block;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.13em;
	text-transform: uppercase;
}

.brand-eyebrow {
	color: #f3b806;
}

.brand-copy h1 {
	max-width: 350px;
	margin: 13px 0 16px;
	font-size: 31px;
	font-weight: 700;
	line-height: 1.18;
	letter-spacing: -0.025em;
}

.brand-copy p {
	max-width: 330px;
	margin: 0;
	color: rgba(255, 255, 255, 0.72);
	font-size: 15px;
	line-height: 1.65;
}

.brand-security {
	margin-top: auto;
	display: flex;
	align-items: center;
	gap: 9px;
	color: rgba(255, 255, 255, 0.78);
	font-size: 13px;
}

.form-panel {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 54px 64px;
	background: #ffffff;
}

.form-content {
	width: 100%;
	max-width: 370px;
}

.mobile-logo-frame {
	display: none;
}

.welcome-icon {
	width: 48px;
	height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 22px;
	border-radius: 14px;
	background: #fff7dc;
	border: 1px solid #f8e5a5;
}

.form-eyebrow {
	margin: 0 0 7px;
	color: #2b5672;
}

.form-content h2 {
	margin: 0;
	color: #101828;
	font-size: 30px;
	font-weight: 700;
	line-height: 1.2;
	letter-spacing: -0.025em;
}

.form-description {
	margin: 9px 0 30px;
	color: #667085;
	font-size: 15px;
}

.login-form ::v-deep .v-input__slot {
	min-height: 54px !important;
	border-radius: 10px !important;
	background: #fbfcfd;
}

.login-form ::v-deep .v-text-field--outlined fieldset {
	border-color: #d6dae1;
}

.login-form ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
	border-width: 2px;
	border-color: #00006f;
}

.login-form ::v-deep .v-input__prepend-inner {
	margin-right: 8px;
}

.login-button {
	height: 52px !important;
	margin-top: 10px;
	border-radius: 10px;
	font-size: 15px;
	font-weight: 700;
	letter-spacing: 0.02em;
	text-transform: none;
	box-shadow: 0 10px 24px rgba(0, 0, 111, 0.20) !important;
}

.forgot-button {
	margin-top: 10px;
	font-weight: 600;
	text-transform: none;
}

.form-security {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	margin-top: 25px;
	padding-top: 20px;
	border-top: 1px solid #eef0f3;
	color: #6b7280;
	font-size: 12px;
}

.page-footer {
	margin: 18px 0 0;
	color: rgba(255, 255, 255, 0.66);
	font-size: 12px;
	letter-spacing: 0.02em;
}

@media (max-width: 820px) {
	.login-container {
		padding: 24px 18px 16px;
	}

	.login-shell {
		max-width: 480px;
		min-height: auto;
		display: block;
	}

	.brand-panel {
		display: none;
	}

	.form-panel {
		flex-direction: column;
		padding: 38px 42px 42px;
	}

	.mobile-logo-frame {
		display: flex;
		width: 210px;
		height: 116px;
		margin: 0 auto 32px;
		border: 1px solid #eceff2;
		border-radius: 14px;
		box-shadow: 0 8px 24px rgba(0, 0, 45, 0.10);
	}

	.mobile-logo {
		width: 205px;
		height: 205px;
		max-width: none;
		flex: 0 0 auto;
		object-fit: contain;
	}

	.welcome-icon {
		display: none;
	}
}

@media (max-width: 600px) {
	.login-container {
		justify-content: flex-start;
		padding-top: 18px;
	}

	.login-shell {
		border-radius: 17px;
	}

	.form-panel {
		padding: 30px 24px 32px;
	}

	.mobile-logo-frame {
		margin-bottom: 25px;
	}

	.form-content h2 {
		font-size: 26px;
	}

	.form-description {
		margin-bottom: 24px;
	}

	.page-footer {
		text-align: center;
	}
}
</style>
